import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { 
  Search, 
  MapPin, 
  Users, 
  Globe,
  Filter,
  School,
  Building,
  Clock,
  Star,
  ExternalLink
} from 'lucide-react';

interface OcSchool {
  name: string;
  location: string;
  places: number;
  type: 'metropolitan' | 'regional' | 'virtual';
  region: string;
  website?: string;
}

// Complete list of OC schools based on NSW Education data
const ocSchools: OcSchool[] = [
  { name: "Alexandra Park Community School", location: "Park Rd, Alexandria", places: 30, type: "metropolitan", region: "Inner Sydney" },
  { name: "Alstonville Public School", location: "Main St, Alstonville", places: 15, type: "regional", region: "North Coast" },
  { name: "Armidale City Public School", location: "Faulkner St, Armidale", places: 15, type: "regional", region: "New England" },
  { name: "Artarmon Public School", location: "McMillan Rd, Artarmon", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Ashfield Public School", location: "Liverpool Rd, Ashfield", places: 30, type: "metropolitan", region: "Inner West" },
  { name: "Aurora College", location: "Rural and remote, virtual opportunity class", places: 100, type: "virtual", region: "Virtual" },
  { name: "Balgowlah Heights Public School", location: "Lewis St, Balgowlah", places: 15, type: "metropolitan", region: "Northern Beaches" },
  { name: "Balmain Public School", location: "Eaton St, Balmain", places: 30, type: "metropolitan", region: "Inner West" },
  { name: "Bathurst West Public School", location: "Suttor St, Bathurst", places: 15, type: "regional", region: "Central West" },
  { name: "Blacktown North Public School", location: "Flushcombe Rd, Blacktown", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Blacktown South Public School", location: "Bungarribee Rd, Blacktown", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Blaxland East Public School", location: "Rickard Rd, Blaxland", places: 30, type: "metropolitan", region: "Blue Mountains" },
  { name: "Bondi Public School", location: "Campbell Parade, Bondi Beach", places: 30, type: "metropolitan", region: "Eastern Suburbs" },
  { name: "Bossley Park Public School", location: "Mimosa Rd, Bossley Park", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Broken Hill Public School", location: "Chloride St, Broken Hill", places: 15, type: "regional", region: "Far West" },
  { name: "Burraneer Bay Public School", location: "Woolooware Rd, Burraneer", places: 30, type: "metropolitan", region: "Sutherland Shire" },
  { name: "Cabramatta Public School", location: "Hill St, Cabramatta", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Camden Public School", location: "Oxley St, Camden", places: 30, type: "metropolitan", region: "Macarthur" },
  { name: "Carlingford Public School", location: "Carlingford Rd, Carlingford", places: 30, type: "metropolitan", region: "Hills District" },
  { name: "Carlingford West Public School", location: "Pennant Hills Rd, Carlingford", places: 30, type: "metropolitan", region: "Hills District" },
  { name: "Castle Cove Public School", location: "Edinburgh Rd, Castle Cove", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Castle Hill Public School", location: "Glenhaven Rd, Castle Hill", places: 30, type: "metropolitan", region: "Hills District" },
  { name: "Chatswood Public School", location: "Centennial Ave, Chatswood", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Chester Hill Public School", location: "Waldron Rd, Chester Hill", places: 30, type: "metropolitan", region: "Canterbury-Bankstown" },
  { name: "Concord Public School", location: "Burwood Rd, Concord", places: 30, type: "metropolitan", region: "Inner West" },
  { name: "Concord West Public School", location: "Flavelle St, Concord West", places: 30, type: "metropolitan", region: "Inner West" },
  { name: "Coogee Public School", location: "Brook St, Coogee", places: 30, type: "metropolitan", region: "Eastern Suburbs" },
  { name: "Cronulla Public School", location: "Elouera Rd, Cronulla", places: 30, type: "metropolitan", region: "Sutherland Shire" },
  { name: "Croydon Public School", location: "The Strand, Croydon", places: 30, type: "metropolitan", region: "Inner West" },
  { name: "Doonside Public School", location: "Eucalyptus Ave, Doonside", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Drummoyne Public School", location: "Marlborough St, Drummoyne", places: 30, type: "metropolitan", region: "Inner West" },
  { name: "Dural Public School", location: "Kenthurst Rd, Dural", places: 30, type: "metropolitan", region: "Hills District" },
  { name: "East Hills Girls Technology High School", location: "Panania Rd, Panania", places: 30, type: "metropolitan", region: "Canterbury-Bankstown" },
  { name: "East Maitland Public School", location: "Lawes St, East Maitland", places: 15, type: "regional", region: "Hunter" },
  { name: "Eastwood Public School", location: "Third Ave, Eastwood", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Epping Public School", location: "Vimiera Rd, Eastwood", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Fairfield Public School", location: "The Crescent, Fairfield", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Fairfield West Public School", location: "Barbara St, Fairfield West", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Forestville Public School", location: "Melwood Ave, Forestville", places: 30, type: "metropolitan", region: "Northern Beaches" },
  { name: "Frenchs Forest Public School", location: "Frenchs Forest Rd, Frenchs Forest", places: 30, type: "metropolitan", region: "Northern Beaches" },
  { name: "Glebe Public School", location: "Derwent St, Glebe", places: 30, type: "metropolitan", region: "Inner Sydney" },
  { name: "Glenwood Public School", location: "Restwell St, Glenwood", places: 30, type: "metropolitan", region: "Hills District" },
  { name: "Gorokan Public School", location: "Wallarah Rd, Gorokan", places: 15, type: "regional", region: "Central Coast" },
  { name: "Gosford Public School", location: "Racecourse Rd, Gosford", places: 15, type: "regional", region: "Central Coast" },
  { name: "Green Valley Public School", location: "Wilson Rd, Green Valley", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Greenacre Public School", location: "Waterloo Rd, Greenacre", places: 30, type: "metropolitan", region: "Canterbury-Bankstown" },
  { name: "Homebush West Public School", location: "Parramatta Rd, Homebush West", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Hornsby North Public School", location: "Rosemead Rd, Hornsby", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Hornsby South Public School", location: "Rosemead Rd, Hornsby", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Hurlstone Park Public School", location: "Floss St, Hurlstone Park", places: 30, type: "metropolitan", region: "Inner West" },
  { name: "Illawong Public School", location: "Bambara St, Illawong", places: 30, type: "metropolitan", region: "Sutherland Shire" },
  { name: "Jannali Public School", location: "Pitt St, Jannali", places: 30, type: "metropolitan", region: "Sutherland Shire" },
  { name: "Kellyville Public School", location: "Kellyville Rd, Kellyville", places: 30, type: "metropolitan", region: "Hills District" },
  { name: "Killara Public School", location: "Koola Ave, Killara", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Lakemba Public School", location: "Wangee Rd, Lakemba", places: 30, type: "metropolitan", region: "Canterbury-Bankstown" },
  { name: "Lane Cove Public School", location: "Longueville Rd, Lane Cove", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Lane Cove West Public School", location: "Rivendell Rd, Lane Cove West", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Leichhardt Public School", location: "Marion St, Leichhardt", places: 30, type: "metropolitan", region: "Inner West" },
  { name: "Lidcombe Public School", location: "John St, Lidcombe", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Liverpool Public School", location: "Moore St, Liverpool", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Liverpool West Public School", location: "Elizabeth Dr, Liverpool", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Maroubra Bay Public School", location: "Donegal St, Maroubra", places: 30, type: "metropolitan", region: "Eastern Suburbs" },
  { name: "Miller Public School", location: "Cartwright Ave, Miller", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Mortdale Public School", location: "Penshurst St, Mortdale", places: 30, type: "metropolitan", region: "St George" },
  { name: "Mosman Public School", location: "Belmont Rd, Mosman", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Mount Pritchard Public School", location: "Elizabeth Dr, Mount Pritchard", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Narwee Public School", location: "Broadarrow Rd, Narwee", places: 30, type: "metropolitan", region: "St George" },
  { name: "Newcastle East Public School", location: "Wolfe St, Newcastle", places: 15, type: "regional", region: "Hunter" },
  { name: "North Rocks Public School", location: "North Rocks Rd, North Rocks", places: 30, type: "metropolitan", region: "Hills District" },
  { name: "North Strathfield Public School", location: "Queen St, North Strathfield", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Northmead Public School", location: "Windsor Rd, Northmead", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Orange Grove Public School", location: "Leeton Ave, Orange Grove", places: 30, type: "metropolitan", region: "Canterbury-Bankstown" },
  { name: "Parramatta Public School", location: "Macquarie St, Parramatta", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Peakhurst Public School", location: "Wyong St, Peakhurst", places: 30, type: "metropolitan", region: "St George" },
  { name: "Pennant Hills Public School", location: "Yarrara Rd, Pennant Hills", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Penshurst Public School", location: "Penshurst St, Penshurst", places: 30, type: "metropolitan", region: "St George" },
  { name: "Prairiewood Public School", location: "Stockton Ave, Prairiewood", places: 30, type: "metropolitan", region: "South Western Sydney" },
  { name: "Putney Public School", location: "Morrison Rd, Putney", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Randwick North Public School", location: "Avoca St, Randwick", places: 30, type: "metropolitan", region: "Eastern Suburbs" },
  { name: "Revesby Public School", location: "Brett St, Revesby", places: 30, type: "metropolitan", region: "Canterbury-Bankstown" },
  { name: "Rosehill Public School", location: "Grand Ave, Rosehill", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Roseville Public School", location: "Hill St, Roseville", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Ryde Public School", location: "Tucker St, Ryde", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Sefton Public School", location: "Ware St, Sefton", places: 30, type: "metropolitan", region: "Canterbury-Bankstown" },
  { name: "Seven Hills Public School", location: "Gipps St, Seven Hills", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Strathfield North Public School", location: "Arthur St, Strathfield", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Summer Hill Public School", location: "Moonbie St, Summer Hill", places: 30, type: "metropolitan", region: "Inner West" },
  { name: "Tamworth Public School", location: "Darling St, Tamworth", places: 15, type: "regional", region: "New England" },
  { name: "Turramurra North Public School", location: "Ku-ring-gai Ave, Turramurra", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Turramurra Public School", location: "Turramurra Ave, Turramurra", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Wahroonga Public School", location: "Burns Rd, Wahroonga", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Waverley Public School", location: "Henrietta St, Waverley", places: 30, type: "metropolitan", region: "Eastern Suburbs" },
  { name: "West Pennant Hills Public School", location: "Hill St, West Pennant Hills", places: 30, type: "metropolitan", region: "Northern Sydney" },
  { name: "Westmead Public School", location: "Darcy Rd, Westmead", places: 30, type: "metropolitan", region: "Western Sydney" },
  { name: "Willoughby Public School", location: "Mowbray Rd, Willoughby", places: 30, type: "metropolitan", region: "North Shore" },
  { name: "Yagoona Public School", location: "Hume Hwy, Yagoona", places: 30, type: "metropolitan", region: "Canterbury-Bankstown" }
];

const regions = Array.from(new Set(ocSchools.map(school => school.region))).sort();

export const OcSchoolsFinder: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 10;

  const filteredSchools = useMemo(() => {
    return ocSchools.filter(school => {
      const matchesSearch = school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           school.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = selectedRegion === 'all' || school.region === selectedRegion;
      const matchesType = selectedType === 'all' || school.type === selectedType;
      
      return matchesSearch && matchesRegion && matchesType;
    });
  }, [searchTerm, selectedRegion, selectedType]);

  const totalPages = Math.ceil(filteredSchools.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedSchools = filteredSchools.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedRegion, selectedType]);

  const totalPlaces = filteredSchools.reduce((sum, school) => sum + school.places, 0);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'metropolitan':
        return <Building className="h-4 w-4" />;
      case 'regional':
        return <School className="h-4 w-4" />;
      case 'virtual':
        return <Globe className="h-4 w-4" />;
      default:
        return <School className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'metropolitan':
        return 'bg-primary text-primary-foreground';
      case 'regional':
        return 'bg-secondary text-secondary-foreground';
      case 'virtual':
        return 'bg-accent text-accent-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const SchoolCard = ({ school }: { school: OcSchool }) => (
    <Card className="h-full hover:shadow-lg transition-shadow border-border">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg leading-tight text-foreground">{school.name}</CardTitle>
          <Badge className={`shrink-0 ${getTypeColor(school.type)}`}>
            {getTypeIcon(school.type)}
            <span className="ml-1 capitalize">{school.type}</span>
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-2 text-muted-foreground">
          <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
          <p className="text-sm leading-relaxed">{school.location}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-foreground">
            {school.places} Year 5 places
          </span>
        </div>
        
        <div className="pt-2">
          <Badge variant="outline" className="text-xs border-primary/20 text-muted-foreground">
            {school.region}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="w-full space-y-6">
      <div className="text-center space-y-4">
        <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground">
          Find NSW Opportunity Class Schools
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Explore {ocSchools.length} Opportunity Class schools across NSW with {ocSchools.reduce((sum, school) => sum + school.places, 0)} available Year 5 places for 2026
        </p>
      </div>

      {/* Search and Filters */}
      <Card className="bg-card">
        <CardContent className="p-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search schools or locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger>
                <SelectValue placeholder="Select region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map(region => (
                  <SelectItem key={region} value={region}>{region}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger>
                <SelectValue placeholder="School type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="metropolitan">Metropolitan</SelectItem>
                <SelectItem value="regional">Regional</SelectItem>
                <SelectItem value="virtual">Virtual (Aurora)</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex gap-2">
              <Button
                variant={view === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('grid')}
                className="flex-1"
              >
                Grid
              </Button>
              <Button
                variant={view === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('list')}
                className="flex-1"
              >
                List
              </Button>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Filter className="h-4 w-4" />
              <span>Found {filteredSchools.length} schools</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>{totalPlaces} available places</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="space-y-4">
        {filteredSchools.length === 0 ? (
          <Card className="bg-card">
            <CardContent className="p-12 text-center">
              <School className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No schools found</h3>
              <p className="text-muted-foreground">Try adjusting your search terms or filters.</p>
            </CardContent>
          </Card>
        ) : view === 'grid' ? (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {paginatedSchools.map((school, index) => (
                <SchoolCard key={`${school.name}-${index}`} school={school} />
              ))}
            </div>
            
            {/* Pagination for Grid View */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(prev => Math.max(prev - 1, 1));
                        }}
                        className={currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(page);
                          }}
                          isActive={currentPage === page}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(prev => Math.min(prev + 1, totalPages));
                        }}
                        className={currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        ) : (
          <>
            <Card className="bg-card">
              <ScrollArea className="h-[600px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">School Name</TableHead>
                      <TableHead className="w-[250px]">Location</TableHead>
                      <TableHead className="w-[150px]">Region</TableHead>
                      <TableHead className="w-[100px]">Type</TableHead>
                      <TableHead className="w-[100px] text-right">Places</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedSchools.map((school, index) => (
                      <TableRow key={`${school.name}-${index}`} className="hover:bg-muted/50">
                        <TableCell className="font-medium">{school.name}</TableCell>
                        <TableCell className="text-muted-foreground">{school.location}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {school.region}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getTypeColor(school.type)} text-xs`}>
                            {getTypeIcon(school.type)}
                            <span className="ml-1 capitalize">{school.type}</span>
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <div className="flex items-center justify-end gap-1">
                            <Users className="h-3 w-3 text-primary" />
                            {school.places}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </Card>

            {/* Pagination for Table View */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(prev => Math.max(prev - 1, 1));
                        }}
                        className={currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(page);
                          }}
                          isActive={currentPage === page}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(prev => Math.min(prev + 1, totalPages));
                        }}
                        className={currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>

      {/* Information Section */}
      <Card className="bg-primary/5">
        <CardContent className="p-6">
          <div className="grid gap-6 md:grid-cols-3">
            <div className="text-center space-y-2">
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Building className="h-6 w-6 text-primary" />
              </div>
              <h4 className="font-semibold text-foreground">Metropolitan Schools</h4>
              <p className="text-sm text-muted-foreground">
                {ocSchools.filter(s => s.type === 'metropolitan').length} schools in Sydney metro area
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="h-12 w-12 bg-secondary/10 rounded-full flex items-center justify-center mx-auto">
                <School className="h-6 w-6 text-secondary" />
              </div>
              <h4 className="font-semibold text-foreground">Regional Schools</h4>
              <p className="text-sm text-muted-foreground">
                {ocSchools.filter(s => s.type === 'regional').length} schools in regional NSW
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="h-12 w-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto">
                <Globe className="h-6 w-6 text-accent" />
              </div>
              <h4 className="font-semibold text-foreground">Aurora College</h4>
              <p className="text-sm text-muted-foreground">
                Virtual opportunity classes for rural students
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legal Disclaimer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-muted-foreground/70 max-w-3xl mx-auto leading-relaxed">
          <strong>Disclaimer:</strong> This list of NSW Opportunity Class schools is current as of August 2025 and is based on our research and understanding of publicly available information. School details, locations, and availability may change. Please verify current information directly with the NSW Department of Education or individual schools before making decisions. Schoolola is not responsible for any inaccuracies or changes to school information.
        </p>
      </div>
    </div>
  );
};